import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { requestChallengeCode, requestChallenge } from './api';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';
import { ModalPortal } from '../modal-portal';
import { getTrackingMetadata } from '../../store/custom-middleware/analytics-middleware';
import { AnalyticsService } from '../../services/analytics-service';
import { VerificationPrompt } from './prompt/';
import { VerificationChallenge } from './challenge/';
import { VerificationResults } from './results/';
import style from './style.module.less';

const verifyStepToComponentMap = {
  0: VerificationPrompt,
  1: VerificationChallenge,
  2: VerificationResults,
};

export const PhoneVerify = ({ close, sellerId, sellerLabelProviderId, setVerification = noop }) => {
  const metadata = useSelector(getTrackingMetadata);
  const track = useCallback(
    (actionName, params) => {
      AnalyticsService.trackEvent(actionName, params, metadata);
    },
    [metadata],
  );

  const [state, setState] = useState({ verifyStep: 0, metadata: {} });

  const onRequestChallengeCode = useCallback(
    (phoneNumber) =>
      requestChallengeCode({
        sellerId,
        phoneNumber,
        sellerProviderId: sellerLabelProviderId,
      }),
    [sellerId, sellerLabelProviderId],
  );

  const onRequestChallenge = useCallback(
    (verificationCode) =>
      requestChallenge({
        sellerId,
        verificationCode,
        publicKey: state.metadata.publicKey,
      }),
    [sellerId, state.metadata.publicKey],
  );

  const goToNextStep = (formStateData = {}) => {
    const { verifyStep } = state;
    if (verifyStep === Object.keys(verifyStepToComponentMap).length - 1) {
      return;
    }
    setState({
      metadata: {
        ...formStateData,
      },
      verifyStep: state.verifyStep + 1,
    });
  };

  const goToPreviousStep = (formStateData = {}) => {
    const { verifyStep } = state;
    if (verifyStep === 0) {
      return;
    }
    setState({
      metadata: {
        ...formStateData,
      },
      verifyStep: state.verifyStep - 1,
    });
  };

  const Component = verifyStepToComponentMap[state.verifyStep];
  return (
    <ModalPortal onClose={close}>
      <Modal className={style['phone-verify-modal-wrapper']}>
        <ModalHeader closeModal={close}>
          <FormattedMessage defaultMessage='Stamps.com Account Verification' description='phoneVerifyModal.header' id='xH31e7' />
        </ModalHeader>
        <Component
          className={style.body}
          closeModal={close}
          goToNextStep={goToNextStep}
          goToPreviousStep={goToPreviousStep}
          isVerified={state.metadata.isVerified}
          phoneNumber={state.metadata.phoneNumber}
          requestChallenge={onRequestChallenge}
          requestChallengeCode={onRequestChallengeCode}
          setVerification={setVerification}
          trackEvent={track}
        />
      </Modal>
    </ModalPortal>
  );
};

PhoneVerify.propTypes = {
  sellerId: PropTypes.string,
  sellerLabelProviderId: PropTypes.string,
  setVerification: PropTypes.func,
  close: PropTypes.func.isRequired,
};
